import { Button, FormControl, FormHelperText, FormLabel, Grid, Input, Stack, Textarea, Typography } from '@mui/joy';
import { DatePicker } from '@mui/x-date-pickers';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { MarkdownSyntaxModal } from '@/components/modals/MarkdownSyntaxModal';
import { ButtonLink } from '@/components/router/ButtonLink';
import { SystemNotification } from '@/components/system-notification/SystemNotification';
import type { CreateSystemNotificationDto } from '@/modules/api';
import { backendApiSystemNotificationsCreateMutation } from '@/modules/api/@tanstack/react-query.gen';

export const Route = createFileRoute('/verwaltung/_global/benachrichtigungen/hinzufuegen')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors, isValid },
  } = useForm<CreateSystemNotificationDto>();

  const { mutateAsync, isPending, error: mutateError } = useMutation(backendApiSystemNotificationsCreateMutation());

  const navigate = useNavigate();

  const previewContent = watch('content');
  const previewTitle = watch('title');

  const [showGuide, setShowGuide] = useState<boolean>(false);

  return (
    <Grid
      container
      component="form"
      spacing={2}
      onSubmit={handleSubmit(async (data) =>
        mutateAsync(
          {
            body: data,
          },
          { onSuccess: async () => navigate({ to: '/verwaltung/benachrichtigungen' }) },
        ),
      )}
    >
      <Grid xs={12}>
        <Typography level="h2">Benachrichtigung hinzufügen</Typography>
      </Grid>
      <Grid xs={6}>
        <FormControl error={!!errors.title}>
          <FormLabel>Titel</FormLabel>
          <Input variant="outlined" {...register('title', { required: true, minLength: 1 })} />
          {errors.title ? <FormHelperText>Pflichtfeld</FormHelperText> : null}
        </FormControl>
      </Grid>

      <Grid xs={3}>
        <Controller
          control={control}
          name="validFrom"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl error={!!errors.validFrom}>
              <FormLabel>Gültig von</FormLabel>

              <DatePicker
                format="DD.MM.YYYY"
                value={value ? dayjs(value) : null}
                onChange={(newValue) => {
                  onChange(newValue ? newValue.toISOString() : undefined);
                }}
                slotProps={{
                  textField: {
                    error: !!errors.validFrom,
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />

              {errors.validFrom ? <FormHelperText>Pflichtfeld</FormHelperText> : null}
            </FormControl>
          )}
        />
      </Grid>

      <Grid xs={3}>
        <Controller
          control={control}
          name="validTo"
          render={({ field: { value, onChange } }) => (
            <FormControl error={!!errors.validTo}>
              <FormLabel>Gültig bis</FormLabel>

              <DatePicker
                format="DD.MM.YYYY"
                value={value ? dayjs(value) : null}
                onChange={(newValue) => {
                  onChange(newValue ? newValue.toISOString() : undefined);
                }}
                slotProps={{
                  textField: {
                    error: !!errors.validTo,
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />

              {errors.validTo ? <FormHelperText>{errors.validTo.message ?? 'Pflichtfeld'}</FormHelperText> : null}
            </FormControl>
          )}
          rules={{
            required: true,
            validate: (value) => {
              const validFrom = getValues('validFrom');
              if (validFrom && value && !dayjs(value).isAfter(dayjs(validFrom))) {
                return 'Muss nach "Gültig von" liegen';
              }

              return true;
            },
          }}
        />
      </Grid>

      <Grid xs={12}>
        <FormControl error={!!errors.content}>
          <FormLabel>
            <Stack alignItems="center" direction="row" gap={2}>
              Inhalt
              <Button onClick={() => setShowGuide(true)} variant="plain">
                Markdown Syntax
              </Button>
            </Stack>
          </FormLabel>

          <Textarea
            variant="outlined"
            slotProps={{
              textarea: {
                minRows: 10,
                ...register('content', { required: true, minLength: 1 }),
              },
            }}
          />
          {errors.content ? <FormHelperText>Pflichtfeld</FormHelperText> : null}
        </FormControl>
        {showGuide ? <MarkdownSyntaxModal onClose={() => setShowGuide(false)} /> : null}
      </Grid>
      <Grid xs={12}>
        <Stack alignItems="center" direction="row" gap={2}>
          <ButtonLink color="neutral" to="/verwaltung/benachrichtigungen" variant="outlined">
            abbrechen
          </ButtonLink>
          <Button disabled={!isValid || isPending} loading={isPending} type="submit">
            speichern
          </Button>
          {mutateError ? <Typography color="danger">{mutateError.message}</Typography> : null}
        </Stack>
      </Grid>
      <Grid xs={8}>
        <FormLabel>Vorschau</FormLabel>
        {previewTitle && previewContent ? (
          <SystemNotification
            preview
            systemNotification={{ title: previewTitle, content: previewContent, id: 'preview' }}
          />
        ) : (
          <Typography>Bitte Titel und Inhalt eingeben</Typography>
        )}
      </Grid>
    </Grid>
  );
}
